import React, { useCallback, useState } from 'react';
import { withTranslation } from 'react-i18next';

import { useOfficeHours } from '../provider';
import {
  convertToDate,
  getTimeFormat,
  generateTimeZoneAbbr,
} from '../../../../../../../helpers/datetime';
import { getWeekDate } from '../helpers';

import ConfirmModal from '../../ConfirmModal';
import SlotView from './SlotView';
import { ISlotProps, SlotActions } from './interfaces';

const Slot = ({ slot, t: translate }: ISlotProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [action, setAction] = useState<SlotActions | null>(null);

  const { bookSlot, cancelSlot, joinSlot } = useOfficeHours();

  const handleBook = useCallback(async () => {
    setAction(null);
    try {
      setIsLoading(true);
      await bookSlot(slot.eventId);
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  }, [slot.eventId, bookSlot]);

  const handleJoin = useCallback(() => {
    joinSlot(slot.eventId);
  }, [slot.eventId]);

  const handleCancel = useCallback(async () => {
    setAction(null);
    try {
      setIsLoading(true);
      await cancelSlot(slot.eventId);
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  }, [slot.eventId, cancelSlot]);

  const renderConfirmationModal = () => {
    if (!action) {
      return null;
    }

    const { startTime, endTime } = slot;

    const startDate = convertToDate(startTime);
    const endDate = convertToDate(endTime);
    const timezoneAbbr = generateTimeZoneAbbr();

    const slotTime = getWeekDate(startDate);

    const getMessage = (message: string) => {
      const messageTime = `${slotTime}, ${getTimeFormat(
        startDate,
      )}–${getTimeFormat(endDate)} ${timezoneAbbr}`;

      return (
        <>
          {message}
          <br /> {messageTime}?
        </>
      );
    };

    switch (action) {
      case SlotActions.BOOK:
        return (
          <ConfirmModal
            open
            message={getMessage('Do you want to book the session on')}
            onConfirm={handleBook}
            onCancel={() => {
              setAction(null);
            }}
          />
        );
      case SlotActions.CANCEL:
        return (
          <ConfirmModal
            open
            message={getMessage('Do you want to cancel the session')}
            onConfirm={handleCancel}
            onCancel={() => {
              setAction(null);
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <SlotView
        slot={slot}
        isLoading={isLoading}
        onBook={() => setAction(SlotActions.BOOK)}
        onCancel={() => setAction(SlotActions.CANCEL)}
        onJoin={handleJoin}
      />
      {renderConfirmationModal()}
    </>
  );
};

export default withTranslation('common')(Slot);
